import React, { memo } from 'react';
import { BookOpen, Mic, Sparkles, Heart, Users, Clock, ArrowRight, Check, Star, Play } from 'lucide-react';
type LandingPageProps = {
  onGetStarted: () => void;
  onTryDemo: () => void;
};
export function LandingPage({
  onGetStarted,
  onTryDemo
}: LandingPageProps) {
  const features = [{
    icon: Mic,
    title: 'Record Stories',
    description: 'Capture precious memories through audio, video, or text. Record anywhere, anytime.',
    color: 'green'
  }, {
    icon: Sparkles,
    title: 'AI-Powered Creation',
    description: 'Transform recordings into beautifully illustrated storybooks with AI magic.',
    color: 'purple'
  }, {
    icon: BookOpen,
    title: 'Beautiful Books',
    description: 'Professional-quality storybooks ready to read, share, and treasure forever.',
    color: 'blue'
  }, {
    icon: Heart,
    title: 'Preserve Legacy',
    description: 'Keep family stories alive for generations. Your memories, their inheritance.',
    color: 'pink'
  }];
  const testimonials = [{
    name: 'Sarah Mitchell',
    role: 'Grandmother of 3',
    image: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=400&h=400&fit=crop',
    quote: "I recorded my grandmother's recipes and stories before she passed. Now my grandchildren can hear her voice and see her memories come to life in beautiful books.",
    rating: 5
  }, {
    name: 'Michael Chen',
    role: 'Father & Storyteller',
    image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=400&fit=crop',
    quote: "My kids love reading the storybooks we create together. It's become our bedtime ritual - recording new adventures and reading old ones.",
    rating: 5
  }, {
    name: 'Emma Rodriguez',
    role: 'Family Historian',
    image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=400&h=400&fit=crop',
    quote: "I've documented 50+ years of family history. Folklore makes it so easy to preserve and share these precious stories with everyone.",
    rating: 5
  }];
  const stats = [{
    value: '10,000+',
    label: 'Stories Recorded'
  }, {
    value: '5,000+',
    label: 'Books Created'
  }, {
    value: '50,000+',
    label: 'Memories Preserved'
  }, {
    value: '4.9/5',
    label: 'User Rating'
  }];
  return <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50" data-id="element-1234">
      {/* Navigation */}
      <nav className="bg-white/80 backdrop-blur-sm border-b border-amber-200 sticky top-0 z-30" data-id="element-1235">
        <div className="max-w-7xl mx-auto px-8 py-4 flex items-center justify-between" data-id="element-1236">
          <div className="flex items-center gap-2" data-id="element-1237">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/vPWEA5aXc2AX6PbtUoaz8G/Folklore-logo.png" alt="Folklore" className="h-8" data-id="element-1238" />
          </div>
          <div className="flex items-center gap-4" data-id="element-1239">
            <button onClick={onTryDemo} className="px-6 py-2 text-gray-700 font-medium hover:text-gray-900 transition-colors" data-id="element-1240">
              Try Demo
            </button>
            <button onClick={onGetStarted} className="px-6 py-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white font-semibold rounded-xl hover:from-green-600 hover:to-emerald-600 transition-all shadow-lg" data-id="element-1241">
              Get Started
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-8 py-20" data-id="element-1242">
        <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-1243">
          <div data-id="element-1244">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-100 rounded-full text-green-700 font-medium mb-6" data-id="element-1245">
              <Sparkles className="w-4 h-4" data-id="element-1246" />
              Preserve Your Family Legacy
            </div>
            <h1 className="text-6xl font-bold text-gray-900 mb-6 leading-tight" data-id="element-1247">
              Turn Family Stories Into
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-600 to-emerald-600" data-id="element-1248">
                {' '}
                Beautiful Books
              </span>
            </h1>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed" data-id="element-1249">
              Record precious memories, create AI-illustrated storybooks, and
              preserve your family's legacy for generations to come.
            </p>
            <div className="flex items-center gap-4" data-id="element-1250">
              <button onClick={onGetStarted} className="flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-green-500 to-emerald-500 text-white font-bold text-lg rounded-xl hover:from-green-600 hover:to-emerald-600 transition-all shadow-xl hover:shadow-2xl" data-id="element-1251">
                Start Recording Free
                <ArrowRight className="w-5 h-5" data-id="element-1252" />
              </button>
              <button onClick={onTryDemo} className="flex items-center gap-2 px-8 py-4 bg-white text-gray-700 font-bold text-lg rounded-xl hover:bg-gray-50 transition-all shadow-lg border-2 border-gray-200" data-id="element-1253">
                <Play className="w-5 h-5" data-id="element-1254" />
                Watch Demo
              </button>
            </div>
            <div className="flex items-center gap-8 mt-8 text-sm text-gray-600" data-id="element-1255">
              <div className="flex items-center gap-2" data-id="element-1256">
                <Check className="w-5 h-5 text-green-600" data-id="element-1257" />
                No credit card required
              </div>
              <div className="flex items-center gap-2" data-id="element-1258">
                <Check className="w-5 h-5 text-green-600" data-id="element-1259" />
                Free to start
              </div>
              <div className="flex items-center gap-2" data-id="element-1260">
                <Check className="w-5 h-5 text-green-600" data-id="element-1261" />
                Cancel anytime
              </div>
            </div>
          </div>
          <div className="relative" data-id="element-1262">
            <div className="absolute inset-0 bg-gradient-to-r from-green-400 to-emerald-400 rounded-3xl blur-3xl opacity-20" data-id="element-1263"></div>
            <div className="relative bg-white rounded-3xl shadow-2xl p-8 transform rotate-2 hover:rotate-0 transition-transform" data-id="element-1264">
              <img src="https://images.unsplash.com/photo-1556910103-1c02745aae4d?w=800&h=600&fit=crop" alt="Family storytelling" className="w-full h-96 object-cover rounded-2xl" data-id="element-1265" />
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-4 flex items-center gap-3" data-id="element-1266">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center" data-id="element-1267">
                  <Sparkles className="w-6 h-6 text-green-600" data-id="element-1268" />
                </div>
                <div data-id="element-1269">
                  <div className="font-bold text-gray-900" data-id="element-1270">AI-Powered</div>
                  <div className="text-sm text-gray-600" data-id="element-1271">
                    Beautiful illustrations
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="max-w-7xl mx-auto px-8 py-12" data-id="element-1272">
        <div className="bg-white rounded-3xl shadow-xl p-12" data-id="element-1273">
          <div className="grid grid-cols-4 gap-8" data-id="element-1274">
            {stats.map((stat, index) => <div key={index} className="text-center" data-id="element-1275">
                <div className="text-4xl font-bold text-gray-900 mb-2" data-id="element-1276">
                  {stat.value}
                </div>
                <div className="text-gray-600" data-id="element-1277">{stat.label}</div>
              </div>)}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-8 py-20" data-id="element-1278">
        <div className="text-center mb-16" data-id="element-1279">
          <h2 className="text-5xl font-bold text-gray-900 mb-4" data-id="element-1280">
            Everything You Need to Preserve Memories
          </h2>
          <p className="text-xl text-gray-600" data-id="element-1281">
            Simple, powerful tools to capture and share your family's stories
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-1282">
          {features.map((feature, index) => <div key={index} className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all hover:-translate-y-1" data-id="element-1283">
              <div className={`w-14 h-14 bg-${feature.color}-100 rounded-2xl flex items-center justify-center mb-4`} data-id="element-1284">
                <feature.icon className={`w-7 h-7 text-${feature.color}-600`} data-id="element-1285" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-1286">
                {feature.title}
              </h3>
              <p className="text-gray-600 leading-relaxed" data-id="element-1287">
                {feature.description}
              </p>
            </div>)}
        </div>
      </section>

      {/* How It Works */}
      <section className="max-w-7xl mx-auto px-8 py-20" data-id="element-1288">
        <div className="text-center mb-16" data-id="element-1289">
          <h2 className="text-5xl font-bold text-gray-900 mb-4" data-id="element-1290">
            Three Simple Steps
          </h2>
          <p className="text-xl text-gray-600" data-id="element-1291">
            From recording to beautiful book in minutes
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-8" data-id="element-1292">
          {[{
          step: '01',
          title: 'Record Your Story',
          description: 'Use your phone or computer to record audio, video, or write text. Share memories, recipes, traditions, or life lessons.',
          icon: Mic,
          color: 'green'
        }, {
          step: '02',
          title: 'AI Creates Your Book',
          description: 'Our AI transforms your recording into a beautifully illustrated storybook with professional-quality images and layout.',
          icon: Sparkles,
          color: 'purple'
        }, {
          step: '03',
          title: 'Share & Treasure',
          description: 'Read online, download PDF, or order printed copies. Share with family and preserve memories forever.',
          icon: BookOpen,
          color: 'blue'
        }].map((item, index) => <div key={index} className="relative" data-id="element-1293">
              <div className="bg-white rounded-2xl p-8 shadow-lg" data-id="element-1294">
                <div className={`text-6xl font-bold text-${item.color}-100 mb-4`} data-id="element-1295">
                  {item.step}
                </div>
                <div className={`w-14 h-14 bg-${item.color}-100 rounded-2xl flex items-center justify-center mb-4`} data-id="element-1296">
                  <item.icon className={`w-7 h-7 text-${item.color}-600`} data-id="element-1297" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3" data-id="element-1298">
                  {item.title}
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-1299">
                  {item.description}
                </p>
              </div>
              {index < 2 && <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2" data-id="element-1300">
                  <ArrowRight className="w-8 h-8 text-gray-300" data-id="element-1301" />
                </div>}
            </div>)}
        </div>
      </section>

      {/* Testimonials */}
      <section className="max-w-7xl mx-auto px-8 py-20" data-id="element-1302">
        <div className="text-center mb-16" data-id="element-1303">
          <h2 className="text-5xl font-bold text-gray-900 mb-4" data-id="element-1304">
            Loved by Families Everywhere
          </h2>
          <p className="text-xl text-gray-600" data-id="element-1305">
            See how Folklore is helping preserve precious memories
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-8" data-id="element-1306">
          {testimonials.map((testimonial, index) => <div key={index} className="bg-white rounded-2xl p-8 shadow-lg" data-id="element-1307">
              <div className="flex items-center gap-1 mb-4" data-id="element-1308">
                {[...Array(testimonial.rating)].map((_, i) => <Star key={i} className="w-5 h-5 text-amber-400 fill-amber-400" data-id="element-1309" />)}
              </div>
              <p className="text-gray-700 mb-6 leading-relaxed italic" data-id="element-1310">
                "{testimonial.quote}"
              </p>
              <div className="flex items-center gap-3" data-id="element-1311">
                <img src={testimonial.image} alt={testimonial.name} className="w-12 h-12 rounded-full object-cover" data-id="element-1312" />
                <div data-id="element-1313">
                  <div className="font-semibold text-gray-900" data-id="element-1314">
                    {testimonial.name}
                  </div>
                  <div className="text-sm text-gray-600" data-id="element-1315">
                    {testimonial.role}
                  </div>
                </div>
              </div>
            </div>)}
        </div>
      </section>

      {/* CTA Section */}
      <section className="max-w-7xl mx-auto px-8 py-20" data-id="element-1316">
        <div className="bg-gradient-to-r from-green-500 to-emerald-500 rounded-3xl p-16 text-center shadow-2xl" data-id="element-1317">
          <h2 className="text-5xl font-bold text-white mb-6" data-id="element-1318">
            Start Preserving Your Family Stories Today
          </h2>
          <p className="text-xl text-green-100 mb-8 max-w-2xl mx-auto" data-id="element-1319">
            Join thousands of families using Folklore to capture and share
            precious memories
          </p>
          <div className="flex items-center justify-center gap-4" data-id="element-1320">
            <button onClick={onGetStarted} className="px-8 py-4 bg-white text-green-600 font-bold text-lg rounded-xl hover:bg-green-50 transition-all shadow-xl" data-id="element-1321">
              Get Started Free
            </button>
            <button onClick={onTryDemo} className="px-8 py-4 bg-green-600 text-white font-bold text-lg rounded-xl hover:bg-green-700 transition-all" data-id="element-1322">
              Try Demo
            </button>
          </div>
          <p className="text-green-100 mt-6" data-id="element-1323">
            No credit card required • Free to start
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 mt-20" data-id="element-1324">
        <div className="max-w-7xl mx-auto px-8 py-12" data-id="element-1325">
          <div className="grid md:grid-cols-4 gap-8" data-id="element-1326">
            <div data-id="element-1327">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/vPWEA5aXc2AX6PbtUoaz8G/Folklore-logo.png" alt="Folklore" className="h-8 mb-4" data-id="element-1328" />
              <p className="text-gray-600 text-sm" data-id="element-1329">
                Preserve your family's legacy with beautiful AI-powered
                storybooks.
              </p>
            </div>
            <div data-id="element-1330">
              <h4 className="font-semibold text-gray-900 mb-4" data-id="element-1331">Product</h4>
              <ul className="space-y-2 text-gray-600 text-sm" data-id="element-1332">
                <li data-id="element-1333">
                  <a href="#" className="hover:text-gray-900" data-id="element-1334">
                    Features
                  </a>
                </li>
                <li data-id="element-1335">
                  <a href="#" className="hover:text-gray-900" data-id="element-1336">
                    Pricing
                  </a>
                </li>
                <li data-id="element-1337">
                  <a href="#" className="hover:text-gray-900" data-id="element-1338">
                    Examples
                  </a>
                </li>
              </ul>
            </div>
            <div data-id="element-1339">
              <h4 className="font-semibold text-gray-900 mb-4" data-id="element-1340">Company</h4>
              <ul className="space-y-2 text-gray-600 text-sm" data-id="element-1341">
                <li data-id="element-1342">
                  <a href="#" className="hover:text-gray-900" data-id="element-1343">
                    About
                  </a>
                </li>
                <li data-id="element-1344">
                  <a href="#" className="hover:text-gray-900" data-id="element-1345">
                    Blog
                  </a>
                </li>
                <li data-id="element-1346">
                  <a href="#" className="hover:text-gray-900" data-id="element-1347">
                    Contact
                  </a>
                </li>
              </ul>
            </div>
            <div data-id="element-1348">
              <h4 className="font-semibold text-gray-900 mb-4" data-id="element-1349">Legal</h4>
              <ul className="space-y-2 text-gray-600 text-sm" data-id="element-1350">
                <li data-id="element-1351">
                  <a href="#" className="hover:text-gray-900" data-id="element-1352">
                    Privacy
                  </a>
                </li>
                <li data-id="element-1353">
                  <a href="#" className="hover:text-gray-900" data-id="element-1354">
                    Terms
                  </a>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-200 mt-12 pt-8 text-center text-gray-600 text-sm" data-id="element-1355">
            © 2024 Folklore. All rights reserved.
          </div>
        </div>
      </footer>
    </div>;
}